import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore,
  Send,
  CheckCircle,
  Error as ErrorIcon,
  Api
} from '@mui/icons-material';
import { authAPI } from '../services/authAPI';
import postsAPI from '../services/postsAPI';
import { contentAPI } from '../services/contentAPI';
import { voiceAPI } from '../services/voiceAPI';
import { websiteAPI } from '../services/websiteAPI';
import { videoAPI } from '../services/videoAPI';
import { trendsAPI } from '../services/trendsAPI';
import { integrationsAPI } from '../services/integrationsAPI';
import { schedulerAPI } from '../services/schedulerAPI';
import { analyticsAPI } from '../services/analyticsAPI';

interface TestResult {
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  response?: any;
  error?: string;
  timestamp?: string;
}

export const ApiTester: React.FC = () => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [testing, setTesting] = useState(false);
  const [selectedApi, setSelectedApi] = useState('all');

  const apiEndpoints = [
    { name: 'Auth - Login', api: 'auth', method: 'login', params: { email: 'test2@example.com', password: 'testpass123' } },
    { name: 'Posts - Get All', api: 'posts', method: 'getAllPosts', params: {} },
    { name: 'Posts - Connection Test', api: 'posts', method: 'testConnection', params: {} },
    { name: 'Content - Generate', api: 'content', method: 'generateContent', params: { topic: 'AI trends', tone: 'professional' } },
    { name: 'Content - Trending Topics', api: 'content', method: 'getTrendingTopics', params: { timeframe: '24h' } },
    { name: 'Voice - Text to Speech', api: 'voice', method: 'textToSpeech', params: { text: 'Hello world' } },
    { name: 'Website - Analyze', api: 'website', method: 'analyzeWebsite', params: { url: 'https://example.com' } },
    { name: 'Video - Generate Script', api: 'video', method: 'generateVideoScript', params: { topic: 'Product demo', platform: 'youtube' } },
    { name: 'Trends - Get Trends', api: 'trends', method: 'getTrends', params: { platform: 'twitter' } },
    { name: 'Integrations - Get All', api: 'integrations', method: 'getAllIntegrations', params: {} },
    { name: 'Scheduler - Get Scheduled', api: 'scheduler', method: 'getScheduledPosts', params: {} },
    { name: 'Analytics - Get Summary', api: 'analytics', method: 'getAnalyticsSummary', params: { timeframe: '7d' } }
  ];

  const apiServices: any = {
    auth: authAPI,
    posts: postsAPI,
    content: contentAPI,
    voice: voiceAPI,
    website: websiteAPI,
    video: videoAPI,
    trends: trendsAPI,
    integrations: integrationsAPI,
    scheduler: schedulerAPI,
    analytics: analyticsAPI
  };

  const testEndpoint = async (endpoint: any) => {
    const testResult: TestResult = {
      endpoint: endpoint.name,
      status: 'pending'
    };

    setResults(prev => [...prev.filter(r => r.endpoint !== endpoint.name), testResult]);

    try {
      const service = apiServices[endpoint.api];
      const method = service[endpoint.method];
      
      if (!method) {
        throw new Error(`Method ${endpoint.method} not found in ${endpoint.api} service`);
      }

      const response = await method(endpoint.params);
      
      setResults(prev => prev.map(r => 
        r.endpoint === endpoint.name 
          ? { 
              ...r, 
              status: 'success', 
              response, 
              timestamp: new Date().toLocaleTimeString() 
            }
          : r
      ));
    } catch (error: any) {
      setResults(prev => prev.map(r => 
        r.endpoint === endpoint.name 
          ? { 
              ...r, 
              status: 'error', 
              error: error.message || 'Unknown error',
              timestamp: new Date().toLocaleTimeString()
            }
          : r
      ));
    }
  };

  const testAllEndpoints = async () => {
    setTesting(true);
    setResults([]);

    const endpointsToTest = selectedApi === 'all' 
      ? apiEndpoints 
      : apiEndpoints.filter(e => e.api === selectedApi);

    for (const endpoint of endpointsToTest) {
      await testEndpoint(endpoint);
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setTesting(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <CircularProgress size={20} />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Api color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            API Tester
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Test all backend API endpoints and verify connectivity
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Configuration
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>API Service</InputLabel>
                <Select
                  value={selectedApi}
                  onChange={(e) => setSelectedApi(e.target.value)}
                  label="API Service"
                >
                  <MenuItem value="all">All Services</MenuItem>
                  <MenuItem value="auth">Authentication</MenuItem>
                  <MenuItem value="posts">Posts</MenuItem>
                  <MenuItem value="content">Content</MenuItem>
                  <MenuItem value="voice">Voice</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="trends">Trends</MenuItem>
                  <MenuItem value="integrations">Integrations</MenuItem>
                  <MenuItem value="scheduler">Scheduler</MenuItem>
                  <MenuItem value="analytics">Analytics</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                fullWidth
                startIcon={testing ? <CircularProgress size={16} /> : <Send />}
                onClick={testAllEndpoints}
                disabled={testing}
              >
                {testing ? 'Testing...' : 'Run Tests'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Summary
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Total Endpoints"
                    secondary={apiEndpoints.length}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Successful"
                    secondary={results.filter(r => r.status === 'success').length}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Failed"
                    secondary={results.filter(r => r.status === 'error').length}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Pending"
                    secondary={results.filter(r => r.status === 'pending').length}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Results
              </Typography>
              {results.length === 0 ? (
                <Typography color="text.secondary">
                  No tests run yet. Click "Run Tests" to start.
                </Typography>
              ) : (
                results.map((result, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ mr: 2 }}>
                          {getStatusIcon(result.status)}
                        </Box>
                        <Typography sx={{ flexGrow: 1 }}>
                          {result.endpoint}
                        </Typography>
                        <Chip
                          label={result.status}
                          color={getStatusColor(result.status)}
                          size="small"
                        />
                        {result.timestamp && (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            {result.timestamp}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {result.status === 'error' && result.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {result.error}
                        </Alert>
                      )}
                      {result.response && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Response:
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={6}
                            value={JSON.stringify(result.response, null, 2)}
                            variant="outlined"
                            InputProps={{
                              readOnly: true,
                              sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                            }}
                          />
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApiTester; 